/*
 * Turning what the Collections wizard holds into a row that POST /sync will
 * accept -- and, just as importantly, into one that cannot wedge the queue.
 *
 * ────────────────────────────────────────────────────────────────────────
 * THE WHITELIST IS THE MOST IMPORTANT THING IN THIS FILE
 * ────────────────────────────────────────────────────────────────────────
 *
 * routes/sync.py refuses a record carrying a column its table does not have.
 * That refusal is `raise SyncError(400, ...)`, and it aborts the ENTIRE
 * request -- not that record, not that table: everything the device had
 * queued, including work from other screens.
 *
 * And the queue only forgets an entry on a 2xx. So one stray key does not
 * lose one collection; it makes every sync from that device fail with the
 * same 400 forever, and nothing anybody does afterwards is ever sent again.
 * The device goes quiet and looks fine.
 *
 * The wizard's state is full of things that are not columns. A vegetation
 * species picked from the dictionary carries `hebrew_name` and
 * `common_name_en` for display. The edit form carries `lab_status` (real) next
 * to whatever a future field adds. So: nothing reaches the queue unless it is
 * named below.
 *
 * ────────────────────────────────────────────────────────────────────────
 * EMPTY STRING IS NOT A VALUE
 * ────────────────────────────────────────────────────────────────────────
 *
 * Every select in the wizard starts at ''. `substrate` is a Postgres ENUM and
 * `gps_lat` is numeric(10,7); '' is not a member of either and the insert
 * errors. Over HTTP this never came up because routes/collections.py writes
 * `body.get("substrate") or None`. Going direct to the table, this file is
 * the only thing standing between a blank dropdown and a failed transaction.
 *
 * ────────────────────────────────────────────────────────────────────────
 * WHAT GOING DIRECT TO THE TABLE GIVES UP
 * ────────────────────────────────────────────────────────────────────────
 *
 * POST /collections validates coordinates, normalises country_code, enforces
 * the public daily limit, mints the public QR code, and sets `collector_id`
 * from the verified token. /sync does none of that -- it upserts the row it
 * is given. So this path is a FALLBACK for when the server cannot be reached,
 * never a replacement for a reachable one: see `isUnreachable`, and the
 * comment above submitNew in pages/Collections.js.
 */

/** Columns of `field_collection`, per 0000_baseline + 0011 + 0010 + 0028. */
export const FIELD_COLLECTION_COLUMNS = new Set([
  'sample_id', 'qr_uid', 'collection_datetime', 'collector_id',
  'gps_lat', 'gps_lng', 'altitude_m',
  'substrate', 'fruiting_body_size', 'phenological_stage',
  'lab_status', 'is_draft', 'is_public_submission', 'review_status',
  'current_taxon_name', 'current_rank', 'current_confidence',
  'general_notes', 'region', 'colour_change',
  'fruiting_body_count', 'fruiting_body_size_estimated', 'growth_pattern',
  'country_code', 'locality_text',
  'host_species', 'microenvironment', 'habitat_biome',
  'updated_at', 'deleted_at',
]);

/** Columns of `sample_vegetation`, per 0000_baseline + 0020 + 0010 + 0028. */
export const SAMPLE_VEGETATION_COLUMNS = new Set([
  'id', 'sample_id', 'species', 'is_dominant',
  'identification_type', 'vegetation_species_id',
  'updated_at', 'deleted_at',
]);

/** Columns of `sample_phenotype`, per 0003 + 0025 + 0010 + 0028.
 *
 *  `recorded_by` is absent on purpose: routes/phenotype.py takes it from the
 *  verified token, and routes/sync.py sets it for a collector. Sending it
 *  would be the client naming who made an observation. */
export const SAMPLE_PHENOTYPE_COLUMNS = new Set([
  'phenotype_id', 'sample_id',
  'odor', 'odor_intensity',
  'colour_change_location', 'colour_change_from', 'colour_change_to',
  'colour_change_delay',
  'stipe_shape', 'stipe_cross_section', 'cap_surface', 'gill_attachment',
  'ring_present', 'ring_type', 'volva_present', 'volva_type',
  'spore_print_colour',
  'cap_colour', 'stipe_colour', 'bruising_reaction', 'bruising_trigger',
  'notes',
  'updated_at', 'deleted_at',
]);

/** Sent as text by the form, wanted as numbers by the column. */
const NUMERIC = new Set(['gps_lat', 'gps_lng', 'altitude_m', 'fruiting_body_count']);

/**
 * A UUID the client can use as a primary key.
 *
 * /sync will not insert a record without one -- `apply_record` cannot tell an
 * insert from an update otherwise -- and it parses what it gets with
 * `uuid.UUID()`, so this must be a real v4 and not a convenient-looking
 * string. The server's own `gen_random_uuid()` is unreachable, which is the
 * whole point of being here.
 */
export function newUuid() {
  const webCrypto = typeof window !== 'undefined' ? window.crypto : undefined;
  if (webCrypto && typeof webCrypto.randomUUID === 'function') return webCrypto.randomUUID();

  const bytes = new Uint8Array(16);
  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    webCrypto.getRandomValues(bytes);
  } else {
    // jsdom without a crypto shim, and nothing else. Collision risk on
    // Math.random is not the concern it would be for a token: these keys are
    // minted a few times a day on one device.
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;   // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80;   // variant 10x
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-`
       + `${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Keep the named columns, drop blanks, make numbers numbers. */
export function clean(source, columns) {
  const out = {};
  for (const [key, value] of Object.entries(source || {})) {
    if (!columns.has(key)) continue;
    if (value === '' || value === undefined) continue;
    if (value !== null && NUMERIC.has(key)) {
      const number = Number(value);
      if (Number.isNaN(number)) continue;
      out[key] = number;
      continue;
    }
    out[key] = value;
  }
  return out;
}

const stamp = (now) => (typeof now === 'function' ? now() : new Date().toISOString());

/**
 * A brand-new collection, as a row.
 *
 * `is_draft` is true because the server's own create path sets it true and
 * nothing here can run the submit validation that clears it -- see
 * routes/collections.py::submit_collection, which counts photos and
 * vegetation before it will flip the flag. A collection queued offline
 * arrives as a draft and still has to be submitted.
 *
 * `collector_id` has to be supplied, because the column is how the app knows
 * whose record this is and no trigger fills it in. Over HTTP the server takes
 * it from the verified token and the client never gets a say; here the client
 * says. That is a real difference and it is noted in the report.
 */
export function newCollectionRecord(form, { collectorId, sampleId, now } = {}) {
  const at = stamp(now);
  return {
    ...clean(form, FIELD_COLLECTION_COLUMNS),
    sample_id: sampleId || form?.sample_id || newUuid(),
    ...(collectorId ? { collector_id: collectorId } : {}),
    is_draft: true,
    updated_at: at,
  };
}

/**
 * An edit to a collection that already exists on the server.
 *
 * No `is_draft` and no `collector_id`: this is a partial update and writing
 * either would silently re-draft somebody's submitted record or move its
 * ownership. Only what the form actually changed goes.
 */
export function collectionEditRecord(sampleId, form, { now } = {}) {
  const fields = clean(form, FIELD_COLLECTION_COLUMNS);
  delete fields.is_draft;
  delete fields.collector_id;
  return { ...fields, sample_id: sampleId, updated_at: stamp(now) };
}

/** The wizard's vegetation list, as rows under one collection. */
export function vegetationRecords(list, { sampleId, now } = {}) {
  const at = stamp(now);
  return (list || []).map((entry) => ({
    ...clean(entry, SAMPLE_VEGETATION_COLUMNS),
    id: entry?.id || newUuid(),
    sample_id: sampleId,
    updated_at: at,
  }));
}

/**
 * The phenotype form, as a row under one collection.
 *
 * Returns null when nothing was recorded, so a caller can skip it rather than
 * queue an empty row: `sample_phenotype` has a CHECK requiring exactly one
 * anchor and nothing else, so a row of all-nulls is a legal, useless record
 * that would then sync forever as a real observation of nothing.
 */
export function phenotypeRecord(phenotype, { sampleId, phenotypeId, now } = {}) {
  const fields = clean(phenotype, SAMPLE_PHENOTYPE_COLUMNS);
  delete fields.phenotype_id;
  delete fields.sample_id;
  if (!Object.keys(fields).length) return null;
  return {
    ...fields,
    phenotype_id: phenotypeId || newUuid(),
    sample_id: sampleId,
    updated_at: stamp(now),
  };
}

/**
 * Did this request fail because nothing answered?
 *
 * The distinction this draws is the safety rail of the whole feature. A
 * network failure means the work is undelivered and belongs in the queue. A
 * 400 means the server looked at it and said no -- "latitude 200 is not a
 * latitude", "you may only edit your own draft" -- and queueing THAT would
 * take a request the validated endpoint refused and re-post it through an
 * endpoint that does not validate. The rejection would come back as a saved
 * record.
 *
 * So: only a request with no response at all is queued. Anything the server
 * answered is shown to the person as the error it is.
 */
export function isUnreachable(error) {
  return !!error && !error.response;
}
